import { useEffect, type ReactNode } from 'react';

import heroUrl from '../../images/login-solar-hero.png';
import logoUrl from '../../images/logo.png';

import layoutStyles from './LoginLayout.module.css';

function LeafIcon({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 3c4.2 2.8 6.5 6.4 6.5 10.5 0 3.8-2.4 6.5-6.5 7.5-4.1-1-6.5-3.7-6.5-7.5C5.5 9.4 7.8 5.8 12 3Zm0 3.2c-2.4 2.2-3.7 4.7-3.9 7.3 1.6-.4 2.8-1.2 3.9-2.5 1.1 1.3 2.3 2.1 3.9 2.5-.2-2.6-1.5-5.1-3.9-7.3Z"
      />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2 5 5v6.5c0 4.2 2.8 7.9 7 9.5 4.2-1.6 7-5.3 7-9.5V5l-7-3Zm0 2.2 5 2.1v5.2c0 3.1-2 5.9-5 7.2-3-1.3-5-4.1-5-7.2V6.3l5-2.1Zm-1.1 4.8 4.2 4.2-1.4 1.4-2.8-2.8-1.4 1.4-1.4-1.4 2.8-2.8Z"
      />
    </svg>
  );
}

function PeopleIcon({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm6.5 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.5 18.5c0-2.8 2.7-4.5 5.5-4.5s5.5 1.7 5.5 4.5V20H3.5v-1.5ZM13 20v-1.4c0-1.5.6-2.7 1.7-3.6 1-.8 2.4-1.2 3.8-1.2 2.2 0 4.5 1.2 4.5 3.8V20H13Z"
      />
    </svg>
  );
}

const heroBenefits = [
  {
    icon: LeafIcon,
    text: 'Sustainable energy access with transparent instalments',
  },
  {
    icon: ShieldIcon,
    text: 'Secure direct debit repayments you can track online',
  },
  {
    icon: PeopleIcon,
    text: 'One place for your application, contract, and payments',
  },
] as const;

export function LoginLayout({
  title,
  subtitle,
  children,
  heroHeadline,
  heroLead,
  scrollableForm = false,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  heroHeadline?: string;
  heroLead?: string;
  scrollableForm?: boolean;
}): React.JSX.Element {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'SEAMONTECH | Solar Repayment Portal';

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className={layoutStyles.shell}>
      <main className={scrollableForm ? layoutStyles.formSideScrollable : layoutStyles.formSide}>
        <div className={layoutStyles.formInner}>
          <div className={layoutStyles.mobileBrand}>
            <img className={layoutStyles.brandLogo} src={logoUrl} alt="" />
            <div>
              <p className={layoutStyles.brandWordmark}>SEAMONTECH</p>
              <p className={layoutStyles.brandMicro}>Clean energy · Brighter tomorrows</p>
            </div>
          </div>

          <header className={layoutStyles.formHeader}>
            <div className={layoutStyles.desktopBrand}>
              <img className={layoutStyles.brandLogo} src={logoUrl} alt="" />
              <div>
                <p className={layoutStyles.brandWordmark}>SEAMONTECH</p>
                <p className={layoutStyles.brandMicro}>Clean energy · Brighter tomorrows</p>
              </div>
            </div>

            <span className={layoutStyles.accentLine} aria-hidden="true" />

            <h1 className={layoutStyles.title} id="auth-heading">
              {title}
            </h1>
            {subtitle !== undefined && <p className={layoutStyles.subtitle}>{subtitle}</p>}
          </header>

          {children}
        </div>
      </main>

      <aside className={layoutStyles.heroSide} aria-hidden="true">
        <img
          className={layoutStyles.heroImage}
          src={heroUrl}
          alt=""
          width={1600}
          height={1200}
          decoding="async"
          fetchPriority="high"
        />
        <div className={layoutStyles.heroGradient} aria-hidden="true" />

        <div className={layoutStyles.heroCopy}>
          <p className={layoutStyles.heroEyebrow}>
            <span>Sustainable energy</span>
            <span className={layoutStyles.heroEyebrowDivider} aria-hidden="true">
              /
            </span>
            <span>for a brighter tomorrow</span>
          </p>
          <h2 className={layoutStyles.heroHeadline}>
            {heroHeadline ?? 'Flexible repayments for a cleaner, brighter future.'}
          </h2>
          <p className={layoutStyles.heroLead}>{heroLead ?? 'Solar financing made simpler.'}</p>

          <ul className={layoutStyles.heroBenefits}>
            {heroBenefits.map(({ icon: Icon, text }) => (
              <li key={text}>
                <span className={layoutStyles.benefitIcon}>
                  <Icon className={layoutStyles.benefitIconSvg} />
                </span>
                <span className={layoutStyles.benefitText}>{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <blockquote className={layoutStyles.quoteCard}>
          <LeafIcon className={layoutStyles.quoteLeaf} />
          <p className={layoutStyles.quoteText}>
            Cleaner energy. Stronger communities.
            <br />A brighter tomorrow.
          </p>
          <footer className={layoutStyles.quoteAttribution}>— SEAMONTECH</footer>
        </blockquote>
      </aside>
    </div>
  );
}
